import type { RequestOptions } from '../client.js';
import {
  type CosmosAccountData,
  type CosmosDelegationsData,
  type CosmosUnbondingDelegationsData,
  type CosmosStakingRewardsData,
  type CosmosValidatorsData,
  type CosmosValidatorData,
  type CosmosStakingPoolData,
  type CosmosBlockResultsData,
  type DenomSupplyData,
  getAccountParamsSchema,
  getDelegationsParamsSchema,
  getUnbondingDelegationsParamsSchema,
  getStakingRewardsParamsSchema,
  getValidatorsParamsSchema,
  getValidatorParamsSchema,
  getStakingPoolParamsSchema,
  getBlockResultsParamsSchema,
  getDenomSupplyParamsSchema,
} from '@spectrum-nodes/api-spec/models';
import type { CosmosChainSlug } from '../types/chains.js';
import { parseParams } from '../internals/validation.js';
import { ChainNamespace } from './base.js';

// Cosmos SDK chain convenience methods, to interact with the Cosmos methods from the API.
export class CosmosNamespace extends ChainNamespace {
  // Auth account info (account number, sequence, pubkey) for a bech32 address.
  async getAccount(
    chain: CosmosChainSlug | undefined,
    address: string,
    options?: RequestOptions,
  ): Promise<CosmosAccountData> {
    const validated = parseParams(getAccountParamsSchema, { chain: this.resolve(chain), address });
    return this.client.jsonRpc<CosmosAccountData>('getAccount', validated, options);
  }

  // A delegator's active delegations, each with its current balance.
  async getDelegations(
    chain: CosmosChainSlug | undefined,
    delegator: string,
    options?: RequestOptions,
  ): Promise<CosmosDelegationsData> {
    const validated = parseParams(getDelegationsParamsSchema, {
      chain: this.resolve(chain),
      delegator,
    });
    return this.client.jsonRpc<CosmosDelegationsData>('getDelegations', validated, options);
  }

  // A delegator's in-progress unbonding delegations and their entries.
  async getUnbondingDelegations(
    chain: CosmosChainSlug | undefined,
    delegator: string,
    options?: RequestOptions,
  ): Promise<CosmosUnbondingDelegationsData> {
    const validated = parseParams(getUnbondingDelegationsParamsSchema, {
      chain: this.resolve(chain),
      delegator,
    });
    return this.client.jsonRpc<CosmosUnbondingDelegationsData>(
      'getUnbondingDelegations',
      validated,
      options,
    );
  }

  // A delegator's outstanding rewards per validator plus the total. cosmoshub, osmosis, axelar, agoric.
  async getStakingRewards(
    chain: CosmosChainSlug | undefined,
    delegator: string,
    options?: RequestOptions,
  ): Promise<CosmosStakingRewardsData> {
    const validated = parseParams(getStakingRewardsParamsSchema, {
      chain: this.resolve(chain),
      delegator,
    });
    return this.client.jsonRpc<CosmosStakingRewardsData>('getStakingRewards', validated, options);
  }

  // The chain's validator set.
  async getValidators(
    chain?: CosmosChainSlug,
    options?: RequestOptions,
  ): Promise<CosmosValidatorsData> {
    const validated = parseParams(getValidatorsParamsSchema, { chain: this.resolve(chain) });
    return this.client.jsonRpc<CosmosValidatorsData>('getValidators', validated, options);
  }

  // A single validator by its `cosmosvaloper...` operator address.
  async getValidator(
    chain: CosmosChainSlug | undefined,
    validatorAddr: string,
    options?: RequestOptions,
  ): Promise<CosmosValidatorData> {
    const validated = parseParams(getValidatorParamsSchema, {
      chain: this.resolve(chain),
      validatorAddr,
    });
    return this.client.jsonRpc<CosmosValidatorData>('getValidator', validated, options);
  }

  // The staking pool totals (bonded vs not-bonded tokens).
  async getStakingPool(
    chain?: CosmosChainSlug,
    options?: RequestOptions,
  ): Promise<CosmosStakingPoolData> {
    const validated = parseParams(getStakingPoolParamsSchema, { chain: this.resolve(chain) });
    return this.client.jsonRpc<CosmosStakingPoolData>('getStakingPool', validated, options);
  }

  // Total supply of a Cosmos denom (e.g. `uatom`, `uosmo`).
  async getDenomSupply(
    chain: CosmosChainSlug | undefined,
    denom: string,
    options?: RequestOptions,
  ): Promise<DenomSupplyData> {
    const validated = parseParams(getDenomSupplyParamsSchema, {
      chain: this.resolve(chain),
      denom,
    });
    return this.client.jsonRpc<DenomSupplyData>('getDenomSupply', validated, options);
  }

  // Tendermint `block_results` for a height (begin/end-block events, per-tx results, validator updates).
  async getBlockResults(
    chain: CosmosChainSlug | undefined,
    block: number,
    options?: RequestOptions,
  ): Promise<CosmosBlockResultsData> {
    const validated = parseParams(getBlockResultsParamsSchema, {
      chain: this.resolve(chain),
      block,
    });
    return this.client.jsonRpc<CosmosBlockResultsData>('getBlockResults', validated, options);
  }
}
